/**
 * Live on-chain oracle autonomy status (auto-refresh timer + protocol sync).
 * Query-only; uses anonymous agent + IDL factories.
 */
import { Actor, type ActorSubclass, type HttpAgent } from "@icp-sdk/core/agent";
import { IDL } from "@icp-sdk/core/candid";
import { createHttpAgent, configFromEnv } from "@motokomaker/ic-client";
import { getEnvMap } from "@/lib/network";
import { getAnonymousAgent, getV2ProtocolActor } from "@/lib/protocol-actor";

export type AssetRefreshStatus = {
	asset: string;
	lastAttemptNs: bigint;
	lastSuccessNs: bigint;
	lastError: string;
	consecutiveFails: bigint;
};

export type OracleRefreshStatus = {
	autoRefreshEnabled: boolean;
	refreshIntervalSecs: bigint;
	effectiveIntervalSecs: bigint;
	timerArmed: boolean;
	xrcDegraded: boolean;
	maxConsecutiveFails: bigint;
	assets: AssetRefreshStatus[];
};

export type ProtocolAutomationHealth = {
	oracleSyncTimerArmed?: boolean;
	oracleAutoSyncEnabled?: boolean;
	oracleSyncIntervalSecs?: bigint;
	lastOracleSyncNs?: bigint;
	oracleFresh?: boolean;
	liqTimerArmed?: boolean;
	backstopTimerArmed?: boolean;
};

export type PriceCallRecord = {
	tsNs: bigint;
	asset: string;
	source: string;
	ok: boolean;
	priceE8: bigint | null;
	error: string;
	numSources: bigint;
	detail: string;
};

export type PriceCallHistoryPage = {
	total: number;
	offset: number;
	limit: number;
	entries: PriceCallRecord[];
};

const AssetRefreshIdl = IDL.Record({
	asset: IDL.Text,
	last_attempt_ns: IDL.Nat64,
	last_success_ns: IDL.Nat64,
	last_error: IDL.Text,
	consecutive_fails: IDL.Nat64,
});

const RefreshStatusIdl = IDL.Record({
	auto_refresh_enabled: IDL.Bool,
	refresh_interval_secs: IDL.Nat64,
	effective_interval_secs: IDL.Nat64,
	timer_armed: IDL.Bool,
	xrc_degraded: IDL.Bool,
	max_consecutive_fails: IDL.Nat64,
	assets: IDL.Vec(AssetRefreshIdl),
});

const PriceCallRecordIdl = IDL.Record({
	ts_ns: IDL.Nat64,
	asset: IDL.Text,
	source: IDL.Text,
	ok: IDL.Bool,
	price_e8: IDL.Opt(IDL.Nat64),
	error: IDL.Text,
	num_sources: IDL.Nat64,
	detail: IDL.Text,
});

const PriceCallHistoryIdl = IDL.Record({
	total: IDL.Nat64,
	offset: IDL.Nat64,
	limit: IDL.Nat64,
	entries: IDL.Vec(PriceCallRecordIdl),
});

const oracleIdlFactory = ({ IDL: I }: { IDL: typeof IDL }) =>
	I.Service({
		get_refresh_status: I.Func([], [RefreshStatusIdl], ["query"]),
		get_price_call_history: I.Func(
			[I.Nat64, I.Nat64],
			[PriceCallHistoryIdl],
			["query"],
		),
	});

type OracleActor = ActorSubclass<{
	get_refresh_status: () => Promise<{
		auto_refresh_enabled: boolean;
		refresh_interval_secs: bigint;
		effective_interval_secs: bigint;
		timer_armed: boolean;
		xrc_degraded: boolean;
		max_consecutive_fails: bigint;
		assets: Array<{
			asset: string;
			last_attempt_ns: bigint;
			last_success_ns: bigint;
			last_error: string;
			consecutive_fails: bigint;
		}>;
	}>;
	get_price_call_history: (
		offset: bigint,
		limit: bigint,
	) => Promise<{
		total: bigint;
		offset: bigint;
		limit: bigint;
		entries: Array<{
			ts_ns: bigint;
			asset: string;
			source: string;
			ok: boolean;
			price_e8: [] | [bigint];
			error: string;
			num_sources: bigint;
			detail: string;
		}>;
	}>;
}>;

function oracleCanisterId(): string | null {
	const env = getEnvMap();
	const id =
		env.VITE_CANISTER_ID_ORACLE?.trim() ||
		env.VITE_ORACLE_CANISTER_ID?.trim() ||
		"";
	return id || null;
}

async function getOracleActor(): Promise<OracleActor | null> {
	const canisterId = oracleCanisterId();
	if (!canisterId) return null;
	let agent: HttpAgent | null = await getAnonymousAgent();
	if (!agent) {
		try {
			const env = getEnvMap();
			const config = configFromEnv(env);
			agent = await createHttpAgent(config);
		} catch {
			return null;
		}
	}
	return Actor.createActor(oracleIdlFactory as never, {
		agent,
		canisterId,
	}) as OracleActor;
}

function optNat64(v: [] | [bigint] | undefined): bigint | null {
	if (!v || v.length === 0) return null;
	return v[0] ?? null;
}

export async function fetchOracleRefreshStatus(): Promise<OracleRefreshStatus | null> {
	try {
		const actor = await getOracleActor();
		if (!actor) return null;
		const raw = await actor.get_refresh_status();
		return {
			autoRefreshEnabled: raw.auto_refresh_enabled,
			refreshIntervalSecs: raw.refresh_interval_secs,
			effectiveIntervalSecs: raw.effective_interval_secs,
			timerArmed: raw.timer_armed,
			xrcDegraded: raw.xrc_degraded,
			maxConsecutiveFails: raw.max_consecutive_fails,
			assets: (raw.assets ?? []).map((a) => ({
				asset: a.asset,
				lastAttemptNs: a.last_attempt_ns,
				lastSuccessNs: a.last_success_ns,
				lastError: a.last_error ?? "",
				consecutiveFails: a.consecutive_fails,
			})),
		};
	} catch (e) {
		console.warn("[oracle-autonomy] get_refresh_status failed:", e);
		return null;
	}
}

/** Newest-first paginated on-chain price check history. */
export async function fetchPriceCallHistory(
	offset: number,
	limit: number,
): Promise<PriceCallHistoryPage | null> {
	try {
		const actor = await getOracleActor();
		if (!actor) return null;
		const raw = await actor.get_price_call_history(
			BigInt(Math.max(0, offset)),
			BigInt(Math.max(1, Math.min(100, limit))),
		);
		return {
			total: Number(raw.total),
			offset: Number(raw.offset),
			limit: Number(raw.limit),
			entries: (raw.entries ?? []).map((e) => ({
				tsNs: e.ts_ns,
				asset: e.asset,
				source: e.source,
				ok: e.ok,
				priceE8: optNat64(e.price_e8),
				error: e.error ?? "",
				numSources: e.num_sources,
				detail: e.detail ?? "",
			})),
		};
	} catch (e) {
		console.warn("[oracle-autonomy] get_price_call_history failed:", e);
		return null;
	}
}

/** Full local date + time with seconds for history rows. */
export function formatCallTimestamp(ns: bigint): string {
	if (ns <= 0n) return "—";
	const ms = Number(ns / 1_000_000n);
	if (!Number.isFinite(ms)) return "—";
	const d = new Date(ms);
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export async function fetchProtocolAutomationHealth(): Promise<ProtocolAutomationHealth | null> {
	try {
		const actor = await getV2ProtocolActor();
		if (!actor?.getAutomationHealth) return null;
		const r = await actor.getAutomationHealth();
		return {
			oracleSyncTimerArmed: r.oracleSyncTimerArmed,
			oracleAutoSyncEnabled: r.oracleAutoSyncEnabled,
			oracleSyncIntervalSecs: r.oracleSyncIntervalSecs,
			lastOracleSyncNs: r.lastOracleSyncNs,
			oracleFresh: r.oracleFresh,
			liqTimerArmed: r.liqTimerArmed,
			backstopTimerArmed: r.backstopTimerArmed,
		};
	} catch (e) {
		console.warn("[oracle-autonomy] getAutomationHealth failed:", e);
		return null;
	}
}

export function formatNsAge(ns: bigint | undefined | null): string {
	if (ns == null || ns <= 0n) return "never";
	const ms = Number(ns / 1_000_000n);
	if (!Number.isFinite(ms) || ms <= 0) return "—";
	const ageMs = Date.now() - ms;
	if (ageMs < 0) return "just now";
	const mins = Math.floor(ageMs / 60_000);
	if (mins < 2) return "just now";
	if (mins < 120) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 48) return `${hrs}h ago`;
	return `${Math.floor(hrs / 24)}d ago`;
}
